import Autorenew from "@mui/icons-material/Autorenew";
import CallMerge from "@mui/icons-material/CallMerge";
import Category from "@mui/icons-material/Category";
import ChangeCircle from "@mui/icons-material/ChangeCircle";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import LocalShipping from "@mui/icons-material/LocalShipping";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import { Box, Paper, Stack, SvgIcon, Typography } from "@mui/material";
import { ContainerEventType, GetWasteContainerQuery } from "generated-graphql/graphql";
import { prettyPrintDateTime } from "util/dates";

import {
  CONTAINER_EVENT_TYPE_LABELS,
  CONTAINER_STATUS_LABELS,
  RECONCILE_CHOICE_LABELS,
  formatContainerDate,
  formatQuantity,
  toGraphqlUnitType,
} from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type ContainerEvent = WasteContainerDetail["events"][number];

/** Icon + color for an audit event, keyed by type. */
const EVENT_VISUALS: Record<ContainerEventType, { icon: typeof SvgIcon; color: string }> = {
  [ContainerEventType.Created]: { icon: FiberManualRecord, color: "text.secondary" },
  [ContainerEventType.Moved]: { icon: SwapHoriz, color: "info.main" },
  [ContainerEventType.StatusChanged]: { icon: SwapHoriz, color: "text.secondary" },
  [ContainerEventType.Consolidated]: { icon: CallMerge, color: "info.main" },
  [ContainerEventType.Reconciled]: { icon: ChangeCircle, color: "success.main" },
  [ContainerEventType.Emptied]: { icon: RemoveCircleOutline, color: "warning.main" },
  [ContainerEventType.Shipped]: { icon: LocalShipping, color: "text.secondary" },
  [ContainerEventType.Reclassified]: { icon: Category, color: "info.main" },
};

const DEFAULT_VISUAL = { icon: Autorenew, color: "text.secondary" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function labelFromRecord(record: Record<string, string>, key: unknown): string | null {
  return typeof key === "string" && key in record ? record[key] : null;
}

/**
 * Events that record when something physically happened carry a user-supplied
 * DATE, stored as UTC midnight — rendering those with a time shows the
 * previous evening to anyone behind UTC. The rest record when the app wrote
 * them, where the time is the useful part.
 */
const DATE_VALUED_EVENT_TYPES: ContainerEventType[] = [
  ContainerEventType.Emptied,
  ContainerEventType.Shipped,
];

/**
 * A one-line human reading of the event's payload, per type. Payloads are
 * loosely-typed JSON, so every read is defensive — an unrecognized shape
 * just renders the bare event label.
 */
function describeEvent(event: ContainerEvent): string | null {
  const payload: unknown = event.payload;
  if (!isRecord(payload)) {
    return null;
  }
  switch (event.type) {
    case ContainerEventType.StatusChanged: {
      const from = labelFromRecord(CONTAINER_STATUS_LABELS, payload.from);
      const to = labelFromRecord(CONTAINER_STATUS_LABELS, payload.to);
      return from && to ? `${from} → ${to}` : null;
    }
    case ContainerEventType.Reconciled: {
      return labelFromRecord(RECONCILE_CHOICE_LABELS, payload.choice);
    }
    case ContainerEventType.Emptied: {
      const amount = payload.amountRemoved;
      const unit = typeof payload.unit === "string" ? toGraphqlUnitType(payload.unit) : null;
      if (typeof amount === "number" && unit) {
        const restarted = payload.clockRestarted === true ? " — clock restarted" : "";
        return `${formatQuantity(amount, unit)} removed${restarted}`;
      }
      return null;
    }
    case ContainerEventType.Consolidated: {
      const sourceIds = payload.sourceIds;
      if (Array.isArray(sourceIds)) {
        const containers = sourceIds.length === 1 ? "container" : "containers";
        return `${sourceIds.length} ${containers} involved`;
      }
      return null;
    }
    case ContainerEventType.Shipped: {
      const requestNumber = payload.manifestRequestNumber;
      const amount = payload.quantity;
      const unit = typeof payload.unit === "string" ? toGraphqlUnitType(payload.unit) : null;
      if (typeof requestNumber !== "string") {
        return null;
      }
      return typeof amount === "number" && unit
        ? `${formatQuantity(amount, unit)} shipped on ${requestNumber}`
        : `Shipped on ${requestNumber}`;
    }
    default:
      return null;
  }
}

function ContainerEventRow({
  event,
  showConnector,
}: {
  event: ContainerEvent;
  showConnector: boolean;
}) {
  const { icon: Icon, color } = EVENT_VISUALS[event.type] ?? DEFAULT_VISUAL;
  const detail = describeEvent(event);

  return (
    <Stack direction="row" spacing={1} alignItems="stretch">
      {/* Timeline rail, matching the fill-history section. */}
      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
        <Icon sx={{ color, fontSize: 20, mt: 0.25 }} />
        {showConnector && <Box sx={{ flex: 1, width: "1px", bgcolor: "grey.300", mt: 0.5 }} />}
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0, pb: 2 }}>
        <Typography variant="subtitle2">{CONTAINER_EVENT_TYPE_LABELS[event.type]}</Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary" display="block">
            {detail}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block">
          {DATE_VALUED_EVENT_TYPES.includes(event.type)
            ? formatContainerDate(event.occurredAt)
            : prettyPrintDateTime(event.occurredAt)}
        </Typography>
      </Box>
    </Stack>
  );
}

export function ContainerHistorySection({ container }: { container: WasteContainerDetail }) {
  const events = container.events;

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: "1px", mb: 2, display: "block" }}
      >
        Activity log
      </Typography>
      {events.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", backgroundColor: "grey.50", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No events recorded yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}>
          {events.map((event, index) => (
            <ContainerEventRow
              key={event.id}
              event={event}
              showConnector={index < events.length - 1}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
}
